import React, { useState, useEffect, useRef } from 'react';
import './Geocoding.css';

// Import API_URL from api.js to ensure consistent API calls
const API_URL = process.env.REACT_APP_API_URL || '';

function Geocoding() {
  const [sheetUrl, setSheetUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [headers, setHeaders] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');

  // Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  // Address component column selections
  const [addressColumn, setAddressColumn] = useState('');
  const [cityColumn, setCityColumn] = useState('');
  const [stateColumn, setStateColumn] = useState('');
  const [zipColumn, setZipColumn] = useState('');
  const [countryColumn, setCountryColumn] = useState('');

  // Configuration
  const [confidenceThreshold, setConfidenceThreshold] = useState(95);
  const [useBackupDetection, setUseBackupDetection] = useState(true);
  const [enableZoning, setEnableZoning] = useState(false);
  const DEFAULT_ZONE_THRESHOLDS = [30, 50];
  const MAX_ZONES = 5;
  const [zoneThresholds, setZoneThresholds] = useState(DEFAULT_ZONE_THRESHOLDS);

  // Progress tracking
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingComplete, setGeocodingComplete] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    progressPercent: 0,
    successful: 0,
    failed: 0,
    usedGeocodio: 0,
    usedGoogle: 0
  });
  const [startTime, setStartTime] = useState(null);
  const [estimatedDuration, setEstimatedDuration] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);

  // Use refs for intervals so we can access them synchronously
  const pollIntervalRef = useRef(null);
  const animationIntervalRef = useRef(null);

  // Collapsible steps
  const [step1Collapsed, setStep1Collapsed] = useState(false);
  const [step2Collapsed, setStep2Collapsed] = useState(false);
  const [step3Collapsed, setStep3Collapsed] = useState(false);
  const [step4Collapsed, setStep4Collapsed] = useState(false);
  const [howItWorksCollapsed, setHowItWorksCollapsed] = useState(true);

  const handleLoadSheet = async (sheetName = null) => {
    if (!sheetUrl.trim()) {
      alert('Please enter a Google Sheet URL');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/geocoding/load-sheet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sheetUrl,
          sheetName: sheetName || selectedSheet || null
        }),
        credentials: 'include'
      });

      const data = await response.json();
      if (!response.ok) {
        if (data.needsSheetsScope) {
          throw new Error('Google Sheets access is required. Go back and use Allow Google Sheets, then try again.');
        }
        throw new Error(data.error || 'Failed to load sheet');
      }

      setHeaders(data.headers);
      setRowCount(data.rowCount);
      setAvailableSheets(data.sheets || []);
      setSelectedSheet(data.selectedSheet || '');

      // Auto-detect columns
      autoDetectColumns(data.headers, data.detectedColumns);
    } catch (err) {
      console.error('Error loading sheet:', err);
      alert(err.message || 'Failed to load sheet. Make sure the URL is correct and you have access.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSheetChange = (newSheetName) => {
    setSelectedSheet(newSheetName);
    // Reload with new sheet
    handleLoadSheet(newSheetName);
  };

  const autoDetectColumns = (headersList, detected = {}) => {
    // Use backend detection or fallback to client-side
    if (detected.address) setAddressColumn(detected.address);
    if (detected.city) setCityColumn(detected.city);
    if (detected.state) setStateColumn(detected.state);
    if (detected.zip) setZipColumn(detected.zip);
    if (detected.country) setCountryColumn(detected.country);
    // Latitude/Longitude will be added as new columns automatically
  };

  const handleStartGeocoding = async () => {
    // Validation
    if (!addressColumn && !cityColumn && !stateColumn && !zipColumn) {
      alert('Please select at least one address component column');
      return;
    }

    if (enableZoning) {
      const nums = zoneThresholds.map(v => Number(v));
      if (nums.some(n => !Number.isFinite(n) || n <= 0)) {
        alert('Zone thresholds must all be positive numbers (miles).');
        return;
      }
      for (let i = 1; i < nums.length; i++) {
        if (nums[i] <= nums[i - 1]) {
          alert('Zone thresholds must be strictly ascending (each cutoff larger than the previous).');
          return;
        }
      }
    }

    setIsGeocoding(true);
    setGeocodingComplete(false);
    setStartTime(Date.now());
    setEstimatedDuration((rowCount * 0.1) * 1.5); // Conservative estimate: 0.1s per address * 1.5 multiplier
    setProgress({
      total: rowCount,
      processed: 0,
      progressPercent: 0, // Start at 0%, will jump to 10% on first poll
      successful: 0,
      failed: 0,
      usedGeocodio: 0,
      usedGoogle: 0
    });

    // Collapse all steps when geocoding starts
    setStep1Collapsed(true);
    setStep2Collapsed(true);
    setStep3Collapsed(true);
    setStep4Collapsed(true);

    try {
      const response = await fetch(`${API_URL}/api/geocoding/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sheetUrl,
          sheetName: selectedSheet || null,
          columns: {
            address: addressColumn,
            city: cityColumn,
            state: stateColumn,
            zip: zipColumn,
            country: countryColumn
          },
          confidenceThreshold,
          useBackupDetection,
          enableZoning,
          zoneThresholds: enableZoning ? zoneThresholds.map(Number) : undefined
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to start geocoding');
      }

      const data = await response.json();
      const sessionId = data.sessionId;

      // Save session ID for cancellation
      setCurrentSessionId(sessionId);

      // Connect to SSE for real-time progress
      connectToProgress(sessionId);
    } catch (err) {
      console.error('Error starting geocoding:', err);
      alert('Failed to start geocoding process');
      setIsGeocoding(false);
    }
  };

  const connectToProgress = (sessionId) => {
    let lastProgressPercent = 0; // Track last received progress from backend
    let lastUpdateTime = Date.now();
    let estimatedDuration = null; // Will be set from first progress update
    let incrementPerSecond = 2; // Default fallback
    console.log('[Frontend] Starting progress polling for session:', sessionId);

    // Incremental progress animation - dynamically calculated based on total addresses
    const animationInterval = setInterval(() => {
      const timeSinceLastUpdate = Date.now() - lastUpdateTime;
      if (timeSinceLastUpdate >= 1000 && lastProgressPercent < 98) {
        // Increment based on calculated rate
        setProgress(prev => {
          // Safety check: ensure we have a valid progressPercent
          const currentPercent = prev.progressPercent || 0;
          const newPercent = Math.min(currentPercent + incrementPerSecond, 98); // Cap at 98%
          console.log(`[Frontend] Animation increment: ${currentPercent.toFixed(1)}% -> ${newPercent.toFixed(1)}% (+${incrementPerSecond.toFixed(2)}%)`);
          lastProgressPercent = newPercent;
          return {
            ...prev,
            progressPercent: newPercent
          };
        });
        lastUpdateTime = Date.now();
      }
    }, 1000);

    // Save interval references for cancellation
    animationIntervalRef.current = animationInterval;

    // Poll for progress updates every 1 second
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/geocoding/progress/${sessionId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          if (response.status === 404) {
            // Session not found or expired
            clearInterval(pollInterval);
            clearInterval(animationInterval);
            setIsGeocoding(false);
            return;
          }
          throw new Error('Failed to fetch progress');
        }

        const data = await response.json();
        console.log('[Frontend] Poll received:', data);

        if (data.type === 'status' || data.type === 'progress') {
          // Update progress
          console.log('[Frontend] Updating progress:', data.processed, '/', data.total, `(${data.progressPercent}%)`);

          // Calculate increment rate on first progress update with total
          if (estimatedDuration === null && data.total) {
            // Estimate: 0.1 seconds per address, multiplied by 0.8 to slow down progress bar
            estimatedDuration = (data.total * 0.1) * 0.8;
            // Geocodio phase is 10-90% = 80% of progress bar
            const geocodioProgressRange = 80;
            // Calculate increment per second: 80% / estimated duration
            incrementPerSecond = Math.min(geocodioProgressRange / estimatedDuration, 5); // Cap at 5% per second
            console.log(`[Frontend] Calculated increment: ${incrementPerSecond.toFixed(2)}% per second (${data.total} addresses, ~${estimatedDuration.toFixed(1)}s estimated)`);
          }

          // Only update if we got a NEW progress value (higher than current)
          if (data.progressPercent !== undefined && data.progressPercent > lastProgressPercent) {
            console.log(`[Frontend] Backend update: ${lastProgressPercent}% -> ${data.progressPercent}%`);
            lastProgressPercent = data.progressPercent;
            lastUpdateTime = Date.now();

            setProgress(prev => ({
              ...prev,
              total: data.total || prev.total,
              processed: data.processed || prev.processed,
              progressPercent: data.progressPercent,
              usedGoogle: data.googleUsage || prev.usedGoogle
            }));
          } else if (data.progressPercent !== undefined && data.progressPercent <= lastProgressPercent) {
            console.log(`[Frontend] Ignoring backend update (not higher): ${data.progressPercent}% (current: ${lastProgressPercent}%)`);
          } else if (data.progressPercent === undefined) {
            // Update other fields if no progressPercent in response
            setProgress(prev => ({
              ...prev,
              total: data.total || prev.total,
              processed: data.processed || prev.processed,
              usedGoogle: data.googleUsage || prev.usedGoogle
            }));
          }
        } else if (data.type === 'complete') {
          // Geocoding complete
          console.log('Geocoding complete:', data.stats);
          setProgress(prev => ({
            ...prev,
            total: data.stats.total,
            processed: data.stats.total,
            progressPercent: 100,
            successful: data.stats.successful,
            failed: data.stats.failed,
            usedGoogle: data.stats.googleUsed
          }));
          setIsGeocoding(false);
          setGeocodingComplete(true);
          clearInterval(pollInterval);
          clearInterval(animationInterval);
        } else if (data.type === 'error') {
          console.error('Geocoding error:', data.message);
          alert(`Error during geocoding: ${data.message}`);
          setIsGeocoding(false);
          clearInterval(pollInterval);
          clearInterval(animationInterval);
        }
      } catch (err) {
        console.error('Error polling progress:', err);
        clearInterval(pollInterval);
        clearInterval(animationInterval);
        setIsGeocoding(false);
      }
    }, 1000); // Poll every 1 second

    // Save poll interval reference for cancellation
    pollIntervalRef.current = pollInterval;
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel the geocoding process?')) {
      return;
    }

    console.log('[Frontend] Cancelling geocoding session:', currentSessionId);

    // Stop polling intervals
    if (pollIntervalRef.current) {
      console.log('[Frontend] Clearing poll interval');
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (animationIntervalRef.current) {
      console.log('[Frontend] Clearing animation interval');
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }

    // Call backend to cancel the session
    if (currentSessionId) {
      try {
        console.log('[Frontend] Calling cancel endpoint for session:', currentSessionId);
        const response = await fetch(`${API_URL}/api/geocoding/cancel/${currentSessionId}`, {
          method: 'POST',
          credentials: 'include'
        });
        const data = await response.json();
        console.log('[Frontend] Cancel response:', data);
      } catch (err) {
        console.error('Error canceling session:', err);
      }
    }

    // Reset UI state
    setIsGeocoding(false);
    setGeocodingComplete(false);
    setCurrentSessionId(null);
    setProgress({
      progressPercent: 0,
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      usedGeocodio: 0,
      usedGoogle: 0
    });
  };

  const handleReset = () => {
    setSheetUrl('');
    setHeaders([]);
    setRowCount(0);
    setAvailableSheets([]);
    setSelectedSheet('');
    setAddressColumn('');
    setCityColumn('');
    setStateColumn('');
    setZipColumn('');
    setCountryColumn('');
    setIsGeocoding(false);
    setGeocodingComplete(false);
    setIsCancelling(false);
    setEnableZoning(false);
    setZoneThresholds(DEFAULT_ZONE_THRESHOLDS);
    setProgress({
      total: 0,
      processed: 0,
      progressPercent: 0,
      successful: 0,
      failed: 0,
      usedGeocodio: 0,
      usedGoogle: 0
    });

    // Expand all steps when resetting
    setStep1Collapsed(false);
    setStep2Collapsed(false);
    setStep3Collapsed(false);
    setStep4Collapsed(false);

    // Reload logs after reset (might have new data)
    loadLogs();
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const response = await fetch(`${API_URL}/api/geocoding/logs`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load logs');
      }

      const data = await response.json();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Error loading logs:', err);
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };


  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Load logs on mount
  useEffect(() => {
    loadLogs();
  }, []);

  // Timer to update remaining time display every second
  useEffect(() => {
    if (isGeocoding && !geocodingComplete) {
      const timer = setInterval(() => {
        // Force re-render to update the remaining time
        setProgress(prev => ({ ...prev }));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isGeocoding, geocodingComplete]);

  return (
    <div className="geocoding-page">
      <div className="geocoding-container">
        <div className="geocoding-header">
          <h1>Bulk Geocoding Tool</h1>
          <p className="geocoding-description">
            Convert addresses in Google Sheets to latitude/longitude coordinates using Geocodio and Google Maps
          </p>
        </div>

        {/* Step 1: Load Sheet */}
        <div className="geocoding-section">
          <h2 onClick={() => setStep1Collapsed(!step1Collapsed)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <span className="step-number">1</span>
            Load Google Sheet
            <span style={{ marginLeft: '10px', fontSize: '0.8em' }}>{step1Collapsed ? '▼' : '▲'}</span>
          </h2>
          {!step1Collapsed && (
          <><div className="input-group">
            <input
              type="text"
              placeholder="Paste Google Sheet URL here..."
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              disabled={isGeocoding}
              className="sheet-url-input"
            />
            <button
              onClick={() => handleLoadSheet()}
              disabled={isLoading || isGeocoding}
              className="btn-primary"
            >
              {isLoading ? 'Loading...' : 'Load Sheet'}
            </button>
          </div>
          {rowCount > 0 && (
            <div>
              <p className="sheet-info">
                ✓ Sheet loaded: {rowCount.toLocaleString()} rows, {headers.length} columns
              </p>
              {availableSheets.length > 1 && (
                <div className="sheet-selector" style={{ marginTop: '10px' }}>
                  <label style={{ marginRight: '10px', fontWeight: '500' }}>
                    Select Tab:
                  </label>
                  <select
                    value={selectedSheet}
                    onChange={(e) => handleSheetChange(e.target.value)}
                    disabled={isGeocoding}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  >
                    {availableSheets.map(sheet => (
                      <option key={sheet.name} value={sheet.name}>
                        {sheet.name} ({sheet.rowCount} rows)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>

        {/* Step 2: Configure Columns */}
        {headers.length > 0 && (
          <div className="geocoding-section">
            <h2 onClick={() => setStep2Collapsed(!step2Collapsed)} style={{ cursor: 'pointer', userSelect: 'none' }}>
              <span className="step-number">2</span>
              Configure Address Columns
              <span style={{ marginLeft: '10px', fontSize: '0.8em' }}>{step2Collapsed ? '▼' : '▲'}</span>
            </h2>

            {!step2Collapsed && (
            <><div className="column-config">
              <div className="config-group">
                <h3>Input Columns (Address Components)</h3>
                <p className="config-hint">Select which columns contain address information</p>

                <div className="column-selectors">
                  <div className="column-selector">
                    <label>Full Address</label>
                    <select
                      value={addressColumn}
                      onChange={(e) => setAddressColumn(e.target.value)}
                      disabled={isGeocoding}
                    >
                      <option value="">-- None --</option>
                      {headers.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>

                  <div className="column-selector">
                    <label>City</label>
                    <select
                      value={cityColumn}
                      onChange={(e) => setCityColumn(e.target.value)}
                      disabled={isGeocoding}
                    >
                      <option value="">-- None --</option>
                      {headers.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>

                  <div className="column-selector">
                    <label>State</label>
                    <select
                      value={stateColumn}
                      onChange={(e) => setStateColumn(e.target.value)}
                      disabled={isGeocoding}
                    >
                      <option value="">-- None --</option>
                      {headers.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>

                  <div className="column-selector">
                    <label>ZIP Code</label>
                    <select
                      value={zipColumn}
                      onChange={(e) => setZipColumn(e.target.value)}
                      disabled={isGeocoding}
                    >
                      <option value="">-- None --</option>
                      {headers.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>

                  <div className="column-selector">
                    <label>Country</label>
                    <select
                      value={countryColumn}
                      onChange={(e) => setCountryColumn(e.target.value)}
                      disabled={isGeocoding}
                    >
                      <option value="">-- None (defaults to US) --</option>
                      {headers.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="output-info" style={{ marginTop: '20px', padding: '15px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #4CAF50' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#2e7d32' }}>
                <strong>📍 Output Columns:</strong> The tool will automatically add {enableZoning ? '7' : '5'} new columns to your sheet:
                <strong> Site Latitude, Site Longitude, Time Zone, UTC Offset, Observes DST</strong>
                {enableZoning && <>, <strong>Nearest Metro, SSN Zone</strong></>}
              </p>
            </div>
            </>
            )}
          </div>
        )}

        {/* Step 3: Configure Settings */}
        {headers.length > 0 && (
          <div className="geocoding-section">
            <h2 onClick={() => setStep3Collapsed(!step3Collapsed)} style={{ cursor: 'pointer', userSelect: 'none' }}>
              <span className="step-number">3</span>
              Geocoding Settings
              <span style={{ marginLeft: '10px', fontSize: '0.8em' }}>{step3Collapsed ? '▼' : '▲'}</span>
            </h2>

            {!step3Collapsed && (
            <><div className="settings-grid">
              <div className="setting-item">
                <label>
                  Confidence Threshold
                  <span className="setting-info">Minimum accuracy required (0-100%)</span>
                </label>
                <div className="threshold-control">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={confidenceThreshold}
                    onChange={(e) => setConfidenceThreshold(Number(e.target.value))}
                    disabled={isGeocoding}
                    className="threshold-slider"
                  />
                  <span className="threshold-value">{confidenceThreshold}%</span>
                </div>
                <p className="setting-hint">
                  Results below this threshold will use Google Maps as backup
                </p>
              </div>

              <div className="setting-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={useBackupDetection}
                    onChange={(e) => setUseBackupDetection(e.target.checked)}
                    disabled={isGeocoding}
                  />
                  <span>
                    Use Google Maps Backup
                    <span className="setting-info">Fallback to Google when Geocodio confidence is low</span>
                  </span>
                </label>
              </div>

              <div className="setting-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={enableZoning}
                    onChange={(e) => setEnableZoning(e.target.checked)}
                    disabled={isGeocoding}
                  />
                  <span>
                    Enable SSN Zoning
                    <span className="setting-info">
                      After geocoding, find the nearest Major Metro and assign a zone.
                      Adds "Nearest Metro" and "SSN Zone" columns. Default: 3 zones (&lt;30 / 30–50 / 50+ mi).
                    </span>
                  </span>
                </label>

                {enableZoning && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f6f8fa', borderRadius: '6px', border: '1px solid #d0d7de' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <label style={{ fontWeight: 500 }}>Number of zones:</label>
                      <select
                        value={zoneThresholds.length + 1}
                        onChange={(e) => {
                          const n = Number(e.target.value);
                          const cutoffCount = n - 1;
                          setZoneThresholds(prev => {
                            const next = prev.slice(0, cutoffCount);
                            while (next.length < cutoffCount) {
                              const last = next.length > 0 ? Number(next[next.length - 1]) : 0;
                              next.push(last > 0 ? last + 20 : 30);
                            }
                            return next;
                          });
                        }}
                        disabled={isGeocoding}
                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        {Array.from({ length: MAX_ZONES }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setZoneThresholds(DEFAULT_ZONE_THRESHOLDS)}
                        disabled={isGeocoding}
                        style={{ padding: '4px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid #d0d7de', background: '#fff', cursor: 'pointer' }}
                      >
                        Reset to default
                      </button>
                    </div>

                    {zoneThresholds.length === 0 ? (
                      <p style={{ margin: 0, fontSize: '13px', color: '#555' }}>
                        Single zone — every successful row gets <strong>SSN Zone 1</strong>.
                      </p>
                    ) : (
                      <div>
                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555' }}>
                          Distance cutoffs in miles (must be ascending). {zoneThresholds.length + 1} zones total.
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                          {zoneThresholds.map((val, idx) => (
                            <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                Zone {idx + 1} / {idx + 2} cutoff
                              </label>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                value={val}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setZoneThresholds(prev => prev.map((p, i) => (i === idx ? (v === '' ? '' : Number(v)) : p)));
                                }}
                                disabled={isGeocoding}
                                style={{ width: '90px', padding: '6px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
                              />
                            </div>
                          ))}
                        </div>
                        <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                          {(() => {
                            const labels = [];
                            for (let i = 0; i <= zoneThresholds.length; i++) {
                              if (i === 0) labels.push(`Zone 1: <${zoneThresholds[0]} mi`);
                              else if (i === zoneThresholds.length) labels.push(`Zone ${i + 1}: ${zoneThresholds[i - 1]}+ mi`);
                              else labels.push(`Zone ${i + 1}: ${zoneThresholds[i - 1]}–${zoneThresholds[i]} mi`);
                            }
                            return labels.join(' • ');
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            </>
            )}
          </div>
        )}

        {/* Step 4: Start Geocoding */}
        {headers.length > 0 && (
          <div className="geocoding-section">
            <h2 onClick={() => setStep4Collapsed(!step4Collapsed)} style={{ cursor: 'pointer', userSelect: 'none' }}>
              <span className="step-number">4</span>
              Start Geocoding
              <span style={{ marginLeft: '10px', fontSize: '0.8em' }}>{step4Collapsed ? '▼' : '▲'}</span>
            </h2>

            {!step4Collapsed && (
            <><div className="action-buttons">
              <button
                onClick={handleStartGeocoding}
                disabled={isGeocoding}
                className="btn-start"
              >
                {isGeocoding ? 'Geocoding in Progress...' : 'Start Geocoding'}
              </button>
              <button
                onClick={handleReset}
                disabled={isGeocoding}
                className="btn-reset"
              >
                Reset
              </button>
            </div>
            </>
            )}
          </div>
        )}

        {/* Progress Tracking */}
        {(isGeocoding || geocodingComplete) && (
          <div className="geocoding-section progress-section">
            <h2>{geocodingComplete ? 'Geocoding Complete' : 'Progress'}</h2>

            <div className="progress-bar-container">
              <div
                className="progress-bar"
                style={{ width: `${progress.progressPercent}%` }}
              >
                <span className="progress-text">
                  {Math.round(progress.progressPercent)}%
                  {isGeocoding && !geocodingComplete && startTime && estimatedDuration && progress.progressPercent < 100 && (
                    <>
                      {' • '}
                      {(() => {
                        const elapsedSeconds = (Date.now() - startTime) / 1000;

                        // Base estimate: (rowCount * 0.1) * 1.5 multiplier
                        // Plus 0.5 seconds for each Google geocoding request
                        const googleAddedTime = (progress.usedGoogle || 0) * 0.5;
                        const totalEstimatedSeconds = estimatedDuration + googleAddedTime;
                        const remainingSeconds = Math.max(0, totalEstimatedSeconds - elapsedSeconds);

                        if (remainingSeconds < 60) {
                          return `${Math.ceil(remainingSeconds)}s remaining`;
                        } else {
                          const minutes = Math.floor(remainingSeconds / 60);
                          const seconds = Math.ceil(remainingSeconds % 60);
                          return `${minutes}m ${seconds}s remaining`;
                        }
                      })()}
                    </>
                  )}
                </span>
              </div>
            </div>

            <div className="progress-stats">
              <div className="stat-item">
                <span className="stat-label">Processed</span>
                <span className="stat-value">{progress.processed} / {progress.total}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Successful</span>
                <span className="stat-value success">{progress.successful}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Failed</span>
                <span className="stat-value error">{progress.failed}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Geocodio Used</span>
                <span className="stat-value">{progress.usedGeocodio}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Google Used</span>
                <span className="stat-value">{progress.usedGoogle}</span>
              </div>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              {geocodingComplete ? (
                <button
                  onClick={handleReset}
                  className="btn-primary"
                  style={{ padding: '12px 32px', fontSize: '16px' }}
                >
                  Complete
                </button>
              ) : isGeocoding && (
                <button
                  onClick={handleCancel}
                  className="btn-cancel"
                  style={{
                    padding: '12px 32px',
                    fontSize: '16px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Cancel Geocoding
                </button>
              )}
            </div>
          </div>
        )}

        {/* How it Works */}
        <div className="geocoding-section">
          <h2 onClick={() => setHowItWorksCollapsed(!howItWorksCollapsed)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            How it works
            <span style={{ marginLeft: '10px', fontSize: '0.8em' }}>{howItWorksCollapsed ? '▼' : '▲'}</span>
          </h2>
          {!howItWorksCollapsed && (
            <ol style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Enter your Google Sheet URL and load the sheet</li>
              <li>Select which columns contain address information</li>
              <li>Configure the confidence threshold - this determines the minimum accuracy score (0-100%) required to accept a geocoding result from Geocodio. Results below this threshold will automatically use Google Maps for better accuracy</li>
              <li>Optionally enable SSN Zoning — after geocoding, each newly geocoded row is matched to the nearest Major Metro (table bq97a8nsn) and assigned a zone. Default bins are <strong>1</strong> for &lt;30 mi, <strong>2</strong> for 30–50 mi, <strong>3</strong> for 50+ mi; you can configure 1–5 zones with custom mile cutoffs</li>
              <li>Start geocoding using Geocodio and Google Maps</li>
              <li>Results are automatically written to 5 new columns: Site Latitude, Site Longitude, Time Zone (EST/CST/PST), UTC Offset, and Observes DST (True/False). With zoning enabled, two additional columns are added: Nearest Metro and SSN Zone</li>
            </ol>
          )}
        </div>

        {/* Geocoding Logs */}
        <div className="geocoding-section">
          <h2>Geocoding History (Last 90 Days)</h2>

          {logsLoading && (
            <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>Loading logs...</p>
          )}

          {logsError && (
            <div className="logs-error">
              Error loading logs: {logsError}
            </div>
          )}

          {!logsLoading && !logsError && logs.length === 0 && (
            <div className="no-logs">
              No geocoding history yet. Complete a geocoding job to see it here.
            </div>
          )}

          {!logsLoading && !logsError && logs.length > 0 && (
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Sheet</th>
                  <th>Rows</th>
                  <th>Successful</th>
                  <th>Failed</th>
                  <th>Google Used</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td data-label="Date">{formatDate(log.timestamp)}</td>
                    <td data-label="User">{log.userName}</td>
                    <td data-label="Sheet" className="log-sheet-cell">
                      <a href={log.sheetUrl} target="_blank" rel="noopener noreferrer">
                        {log.sheetName} - {log.tabName}
                      </a>
                    </td>
                    <td data-label="Rows">{log.rowCount}</td>
                    <td data-label="Successful" style={{ color: '#4caf50', fontWeight: 600 }}>{log.successful}</td>
                    <td data-label="Failed" style={{ color: log.failed > 0 ? '#f44336' : '#666', fontWeight: 600 }}>{log.failed}</td>
                    <td data-label="Google Used">{log.googleUsed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Geocoding;
